import type { CanvasEntity, EntityKind, ConnectorEndpoint, StrokeStyle, ArrowMarkerType } from './entityTypes';

// ============================================
// Layer Types
// ============================================

export interface CanvasLayer {
  id: string;
  name: string;
  visible: boolean;
  locked: boolean;
  // Order is determined by array position in layers[].
  // Lower index = rendered first (behind). Higher index = in front.
}

export function generateLayerId(): string {
  return `layer-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

// ============================================
// TextBlock Constants
// ============================================

export const DEFAULT_TEXTBLOCK_WIDTH = 200;
export const DEFAULT_TEXTBLOCK_HEIGHT = 80;
export const MIN_TEXTBLOCK_WIDTH = 60;
export const MIN_TEXTBLOCK_HEIGHT = 30;

export function generateTextBlockId(): string {
  return `tb-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

// ============================================
// TextLabel Constants
// ============================================

export function generateTextLabelId(): string {
  return `tl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

export function generateSpriteId(): string {
  return `sp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

export function generateImageId(): string {
  return `img-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

export function generateBlockNodeId(): string {
  return `bn-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

export function generateShapeId(): string {
  return `shp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

export function generateAdrTriggerId(): string {
  return `adr-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

// ============================================
// Hit-test: find entity at canvas point
// ============================================

export function findEntityAtPoint(
  canvasPoint: { x: number; y: number },
  entities: import('./entityTypes').CanvasEntity[],
): import('./entityTypes').CanvasEntity | null {
  const excludeKinds = new Set(['waypoint', 'connector', 'adr-trigger']);
  let best: import('./entityTypes').CanvasEntity | null = null;
  let bestArea = Infinity;
  for (const entity of entities) {
    if (excludeKinds.has(entity.kind)) continue;
    if (canvasPoint.x < entity.x || canvasPoint.x > entity.x + entity.width) continue;
    if (canvasPoint.y < entity.y || canvasPoint.y > entity.y + entity.height) continue;
    const area = entity.width * entity.height;
    if (area < bestArea) { best = entity; bestArea = area; }
  }
  return best;
}

// ============================================
// Connector Types & Helpers
// ============================================

export type ConnectorSide = 'top' | 'right' | 'bottom' | 'left';
export const ALL_CONNECTOR_SIDES: ConnectorSide[] = ['top', 'right', 'bottom', 'left'];

export function getConnectorPointForRect(
  x: number,
  y: number,
  width: number,
  height: number,
  side: ConnectorSide,
): { x: number; y: number } {
  switch (side) {
    case 'top':    return { x: x + width / 2, y: y };
    case 'right':  return { x: x + width, y: y + height / 2 };
    case 'bottom': return { x: x + width / 2, y: y + height };
    case 'left':   return { x: x, y: y + height / 2 };
  }
}

export function getConnectorPoint(
  nodeX: number,
  nodeY: number,
  side: ConnectorSide,
): { x: number; y: number } {
  return getConnectorPointForRect(nodeX, nodeY, NODE_WIDTH, NODE_HEIGHT, side);
}

export function getConnectorDirection(side: ConnectorSide): { dx: number; dy: number } {
  switch (side) {
    case 'top':    return { dx: 0, dy: -1 };
    case 'right':  return { dx: 1, dy: 0 };
    case 'bottom': return { dx: 0, dy: 1 };
    case 'left':   return { dx: -1, dy: 0 };
  }
}

function findNearestConnectorForRect(
  canvasPoint: { x: number; y: number },
  x: number,
  y: number,
  width: number,
  height: number,
): { side: ConnectorSide; point: { x: number; y: number }; dist: number } {
  let best: { side: ConnectorSide; point: { x: number; y: number }; dist: number } | null = null;
  for (const side of ALL_CONNECTOR_SIDES) {
    const pt = getConnectorPointForRect(x, y, width, height, side);
    const dist = Math.hypot(canvasPoint.x - pt.x, canvasPoint.y - pt.y);
    if (!best || dist < best.dist) {
      best = { side, point: pt, dist };
    }
  }
  return best!;
}

// ============================================
// Segment Types (replaces Edge types)
// ============================================

export type SegmentType = 'bezier' | 'straight' | 'ortho90' | 'angle45' | 'elbowed';

/** Unified endpoint: entityId + side ('center' for waypoints) */
export type SegmentEndpoint = ConnectorEndpoint;

export interface CanvasSegment {
  id: string;
  from: ConnectorEndpoint;
  to: ConnectorEndpoint;
  segmentType: SegmentType;
  // Styling
  strokeColor?: string;
  strokeWidth?: number;
  strokeStyle?: StrokeStyle;
  arrowStart?: ArrowMarkerType;
  arrowEnd?: ArrowMarkerType;
  label?: string;
  labelOffsetX?: number;
  labelOffsetY?: number;
}

export const WAYPOINT_RADIUS = 8;

export function generateSegmentId(): string {
  return `seg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

/** Rect map for resolving endpoint positions */
export type EntityRectMap = Map<string, { x: number; y: number; width: number; height: number }>;

export function resolveEndpointPosition(
  endpoint: ConnectorEndpoint,
  entityRectMap: EntityRectMap,
): { x: number; y: number } | null {
  const rect = entityRectMap.get(endpoint.entityId);
  if (!rect) return null;
  if (endpoint.side === 'center') return { x: rect.x, y: rect.y };
  return getConnectorPointForRect(rect.x, rect.y, rect.width, rect.height, endpoint.side);
}

export function resolveEndpointDirection(
  endpoint: ConnectorEndpoint,
  endpointPos: { x: number; y: number },
  otherPos: { x: number; y: number },
): { dx: number; dy: number } {
  if (endpoint.side !== 'center') {
    return getConnectorDirection(endpoint.side);
  }
  const dx = otherPos.x - endpointPos.x;
  const dy = otherPos.y - endpointPos.y;
  const len = Math.hypot(dx, dy) || 1;
  return { dx: dx / len, dy: dy / len };
}

export interface DropTarget {
  entityId: string;
  kind: EntityKind;
  side: ConnectorSide | 'center';
  dist: number;
}

export function dropTargetToEndpoint(target: DropTarget): ConnectorEndpoint {
  return { entityId: target.entityId, side: target.side };
}

export function findDropTargetUnified(
  canvasPoint: { x: number; y: number },
  entities: CanvasEntity[],
  excludeEntityId?: string,
): DropTarget | null {
  let best: DropTarget | null = null;
  const HIT = 20;

  for (const entity of entities) {
    if (entity.id === excludeEntityId) continue;

    if (entity.kind === 'waypoint') {
      const dist = Math.hypot(canvasPoint.x - entity.x, canvasPoint.y - entity.y);
      if (dist < HIT && (!best || dist < best.dist)) {
        best = { entityId: entity.id, kind: 'waypoint', side: 'center', dist };
      }
      continue;
    }

    if (!entity.connectable) continue;

    const nearest = findNearestConnectorForRect(canvasPoint, entity.x, entity.y, entity.width, entity.height);
    if (nearest.dist >= HIT || (best && nearest.dist >= best.dist)) continue;

    best = { entityId: entity.id, kind: entity.kind, side: nearest.side, dist: nearest.dist };
  }
  return best;
}

// ============================================
// Group Types
// ============================================

export interface CanvasGroup {
  id: string;
  label: string;
  description?: string;
  backgroundColor: string;
  collapsed: boolean;
  collapsedX?: number;
  collapsedY?: number;
  memberEntityIds: string[];
  memberNodeIds: string[];
  memberGroupIds: string[];
  layerId: string;
  displayMode: 'invisible' | 'container';
  borderColor?: string;
  borderWidth?: number;
}

export interface GroupBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const COLLAPSED_GROUP_WIDTH = 160;
export const COLLAPSED_GROUP_HEIGHT = 40;
export const GROUP_PADDING = 40;

export function generateGroupId(): string {
  return `grp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

export function computeGroupBounds(
  group: CanvasGroup,
  memberRectMap: Map<string, { x: number; y: number; width: number; height: number }>,
  groupBoundsMap: Map<string, GroupBounds>,
  padding = GROUP_PADDING,
  hiddenNodeIds?: Set<string>,
): GroupBounds | null {
  if (group.collapsed) {
    return {
      x: group.collapsedX ?? 0,
      y: group.collapsedY ?? 0,
      width: COLLAPSED_GROUP_WIDTH,
      height: COLLAPSED_GROUP_HEIGHT,
    };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let hasContent = false;

  for (const nodeId of group.memberNodeIds) {
    if (hiddenNodeIds && hiddenNodeIds.has(nodeId)) continue;
    const rect = memberRectMap.get(nodeId);
    if (!rect) continue;
    hasContent = true;
    minX = Math.min(minX, rect.x);
    minY = Math.min(minY, rect.y);
    maxX = Math.max(maxX, rect.x + rect.width);
    maxY = Math.max(maxY, rect.y + rect.height);
  }

  for (const childGroupId of group.memberGroupIds) {
    const childBounds = groupBoundsMap.get(childGroupId);
    if (!childBounds) continue;
    hasContent = true;
    minX = Math.min(minX, childBounds.x);
    minY = Math.min(minY, childBounds.y);
    maxX = Math.max(maxX, childBounds.x + childBounds.width);
    maxY = Math.max(maxY, childBounds.y + childBounds.height);
  }

  if (!hasContent) return null;

  const headerOffset = group.displayMode === 'container' ? 24 : 0;
  return {
    x: minX - padding,
    y: minY - padding - headerOffset,
    width: maxX - minX + padding * 2,
    height: maxY - minY + padding * 2 + headerOffset,
  };
}

// ============================================
// Canvas Types
// ============================================

export interface Canvas {
  id: string;
  name: string;
  boardId: string;
  rotation?: number; // 0 | 90 | 180 | 270, default 0
}

export interface CanvasNode {
  taskId: string;
  x: number;
  y: number;
  layerId: string;
}

export interface CanvasStoreContext {
  canvas: Canvas;
  activeLayerId: string;
  layers: CanvasLayer[];
  nodes: Record<string, import('./entityTypes').CanvasEntity>;
  connectors: Record<string, import('./entityTypes').CanvasEntity>;
  groups: Record<string, import('./entityTypes').CanvasEntity>;
  nodesVersion: number;       // +1 bei node add/remove
  connectorsVersion: number;  // +1 bei connector add/remove
  groupsVersion: number;      // +1 bei group add/remove
}

// ============================================
// Connection Drag State
// ============================================

export interface ConnectionDragState {
  fromEndpoint: SegmentEndpoint;
  fromPoint: { x: number; y: number };
  currentPoint: { x: number; y: number };
}

// ============================================
// Edge Redock Drag State
// ============================================

export interface RedockDragState {
  /** Which end of the segment is being dragged */
  end: 'from' | 'to';
  /** The fixed (anchor) end */
  anchorEndpoint: SegmentEndpoint;
  anchorPoint: { x: number; y: number };
  /** Current cursor position in canvas coords */
  currentPoint: { x: number; y: number };
}

// ============================================
// Pan/Zoom State
// ============================================

export interface PanZoomState {
  panX: number;
  panY: number;
  zoom: number;
}

// ============================================
// Constants
// ============================================

export const NODE_WIDTH = 200;
export const NODE_HEIGHT = 60;
export const CONNECTOR_RADIUS = 6;
export const GRID_SIZE = 20;
export const ZOOM_MIN = 0.1;
export const ZOOM_MAX = 5.0;

// ============================================
// Canvas coordinate conversion
// ============================================

export function screenToCanvas(
  screenX: number,
  screenY: number,
  container: HTMLElement,
  panZoom: PanZoomState,
  rotationDeg: number = 0,
  cachedRect?: DOMRect,
): { x: number; y: number } {
  const rect = cachedRect ?? container.getBoundingClientRect();
  const tx = (screenX - rect.left - panZoom.panX) / panZoom.zoom;
  const ty = (screenY - rect.top - panZoom.panY) / panZoom.zoom;
  if (rotationDeg === 0) return { x: tx, y: ty };
  const rad = (-rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: tx * cos - ty * sin,
    y: tx * sin + ty * cos,
  };
}
